import { getJSON } from '@/functions/get-json';
import { faBooks, faChevronLeft, faMagnifyingGlass } from '@fortawesome/sharp-solid-svg-icons';
import { Labels } from '@lib/sc-eone-ui/src/strings/strings-en-us';
import { HelpArticleRenderer } from 'eone-help-renderer/help-article/help-article-renderer';
import { Button } from 'eone-ui/buttons/button';
import { useHelpContext } from 'eone-ui/help-context';
import { InputString } from 'eone-ui/inputs/input-string';
import { Sidebar } from 'eone-ui/sidebar/sidebar';
import Fuse from 'fuse.js';
import { useEffect, useState } from 'react';
import './help-sidebar.css';

function HelpSearch(props) {
  const getContent = () => {
    if (!props.ArticleResults || props.ArticleResults.length === 0) {
      return <span className="eone-ui-modal-message">{'No results found'}</span>;
    }

    return (
      <div>
        {props.ArticleResults.map((result, index) => (
          <HelpArticleResults
            key={index}
            Type={'Article'}
            Name={result.Name}
            Slug={result.Slug}
            Children={result.Children}
          />
        ))}
      </div>
    );
  };

  if (!props.Visible) {
    return null;
  }

  return (
    <div className="pd-help-search-container">
      <div className="pd-help-search-string-container">
        <InputString
          Icon={faMagnifyingGlass}
          AutoFocus
          Placeholder={Labels.Search}
          Value={props.SearchValue}
          OnChange={props.ChangeSearchValue}
        />
      </div>
      {getContent()}
    </div>
  );
}

function HelpArticleResults(props) {
  const helpContext = useHelpContext();

  return (
    <div style={{ margin: '1em' }}>
      <h1>
        <Button
          Label={props.Name}
          OnClick={() => helpContext.push({ Type: props.Type, Id: props.Slug })}
        />
      </h1>
      <div style={{ marginLeft: '1em' }}>
        {props.Children &&
          props.Children.map((article, index) => (
            <HelpArticleResults
              key={index}
              Name={article.Name}
              Type={props.Type}
              Slug={article.Slug}
              Children={article.Children}
            />
          ))}
      </div>
    </div>
  );
}

function Help(props) {
  const { LoadHelp, SettingsBaseUrl, OpenHelpSidebar } = props;
  const helpContext = useHelpContext();
  const [view, setView] = useState('documentation');
  const [article, setArticle] = useState<any>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    LoadHelp();
  }, [LoadHelp]);

  useEffect(() => {
    if (helpContext.top !== null) {
      switch (helpContext.top.Type) {
        case 'Article':
          getJSON(
            SettingsBaseUrl,
            'documentation',
            `help-articles/${helpContext.top.Id}.json`,
            loadArticle,
          );
          break;
      }
      OpenHelpSidebar();
    } else {
      setArticle(null);
    }
  }, [helpContext.top, SettingsBaseUrl, OpenHelpSidebar]);

  const loadArticle = (articleJson) => {
    setArticle(articleJson);
    setView('documentation');
  };

  const changeSelectedItem = (id, type) => {
    props.OpenHelpSidebar();
    helpContext.push({ Id: id, Type: type });
  };

  const showSearch = () => {
    setView('search');
  };

  const close = () => {
    helpContext.clear();
    props.OnClose();
  };

  const back = () => {
    const newTop = helpContext.pop();
    if (!newTop || !newTop.Id) {
      home();
    } else {
      switch (helpContext.top.Type) {
        case 'Article':
          getJSON(
            props.SettingsBaseUrl,
            'documentation',
            `help-articles/${newTop.Id}.json`,
            loadArticle,
          );
          break;
      }
    }
  };

  const home = () => {
    setView('documentation');
    setArticle(null);
    helpContext.clear();
  };

  if (!props.IsOpen) {
    return null;
  }

  //const getHomePageArticles = () => {
  //    return props.Help.map(item => ({
  //        link: item.File,
  //        name: item.Name,
  //        pinToTop: item.PinToTop
  //    }));
  //};

  const fuseSearch = (data, search) => {
    const options = {
      isCaseSensitive: false,
      shouldSort: true,
      threshold: 0.25,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      findAllMatches: true,
      useExtendedSearch: true,
      keys: [
        { name: 'Name', weight: 0.8 },
        { name: 'Description', weight: 0.2 },
      ],
    };
    const fuse = new Fuse<any>(data, options);

    const words = search.split(' ');
    const combinedResults = data.map((item) => ({ item, count: 0 }));

    words.forEach((word) => {
      const results = fuse.search(word);
      results.forEach((item) => {
        const index = combinedResults.findIndex(
          (combined) => combined.item.Slug === item.item.Slug,
        );
        if (index !== -1) {
          combinedResults[index].count++;
        }
      });
    });

    return combinedResults
      .filter((result) => result.count === words.length)
      .map((result) => result.item);
  };

  const getArticleSearchItems = (articles) => {
    const searchItems = [
      ...articles.map((article) => ({
        Slug: article.Slug,
        Name: article.Name,
      })),
      ...articles
        .filter((a) => a.Children && a.Children.length > 0)
        .flatMap((article) => getArticleSearchItems(article.Children)),
    ];
    return searchItems;
  };

  const getLeafArticlesWithSlugs = (articleItems, slugList) => {
    const leafArticles: any[] = [];
    for (const articleItem of articleItems) {
      if (articleItem.Children && articleItem.Children.length > 0) {
        leafArticles.push(...getLeafArticlesWithSlugs(articleItem.Children, slugList));
      } else if (slugList.includes(articleItem.Slug)) {
        leafArticles.push(articleItem);
      }
    }
    return leafArticles;
  };

  //const getArticlesWithSlugs = (articles, slugList) => {
  //    return articles.filter(article => slugList.includes(article.Slug) || (article.Children && article.Children.some(child => slugList.includes(child.Slug))))
  //        .map(article => ({
  //            ...article,
  //            Children: getArticlesWithSlugs(article.Children, slugList)
  //        }));
  //};

  const getArticleSearchResults = () => {
    if (!searchValue || searchValue.trim() === '') {
      return [];
    }
    const slugs = getArticleSearchItems(props.Help);

    const foundSlugs = fuseSearch(slugs, searchValue).map((s) => s.Slug);
    const results = getLeafArticlesWithSlugs(props.Help, foundSlugs);
    return results;
  };

  const getHeaderButtons = () => {
    return [
      {
        Title: 'Table of contents',
        Icon: faBooks,
        OnClick: home,
        DataTestId: 'helpHeader_home',
      },
      {
        Title: 'Search',
        Icon: faMagnifyingGlass,
        OnClick: showSearch,
        DataTestId: 'helpHeader_search',
      },
    ];
  };

  const getPreTitleButtons = () => {
    return [
      {
        Visible: article !== null,
        Title: Labels.GoBack,
        Icon: faChevronLeft,
        OnClick: back,
        DataTestId: 'helpHeader_back',
      },
    ];
  };

  return (
    <Sidebar
      HeaderTitle={Labels.Help}
      HeaderButtons={getHeaderButtons()}
      Close={close}
      PreTitleButtons={getPreTitleButtons()}
    >
      <HelpSearch
        Visible={view === 'search'}
        SearchString={searchValue}
        ChangeSearchValue={setSearchValue}
        ArticleResults={getArticleSearchResults()}
      />
      <HelpArticleRenderer
        Visible={view === 'documentation' && article === null}
        Title={Labels.SmartConnectDocumentation}
        Description="Table of contents"
        Articles={props.Help}
        Theme="sidebar"
        SelectedItem={null}
        BaseUrl={props.SettingsBaseUrl}
        ChangeSelectedItem={changeSelectedItem}
      />
      <HelpArticleRenderer
        Visible={view === 'documentation' && article !== null}
        Name={article?.Title}
        SelectedItem={article}
        Description={article?.Description}
        Items={article?.Items}
        Articles={article?.Articles}
        RelatedArticles={article?.RelatedArticles}
        Theme="sidebar"
        BaseUrl={props.SettingsBaseUrl}
        ChangeSelectedItem={changeSelectedItem}
      />
    </Sidebar>
  );
}

export { Help };
